/**
 * 认证API服务
 * 封装所有认证相关的API调用
 */

import { apiClient } from './ApiClient';
import type { User } from '../types/user.types';
import { logger } from '../utils/logger';

export interface LoginRequest {
  provider: string;
  credentials: any;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: User;
  isNewUser: boolean;
}

export interface SessionResponse {
  user: User;
}

export interface MeResponse {
  user: User;
  account: {
    balance: number;
    availableBalance: number;
    frozenMargin: number;
    totalDeposit: number;
    totalWithdrawal: number;
    totalPnl: number;
    totalFee: number;
    totalTrades: number;
    winningTrades: number;
  };
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export class AuthApiService {
  /**
   * 登录/注册
   */
  async login(provider: string, credentials: any): Promise<LoginResponse | null> {
    try {
      const response = await apiClient.post<LoginResponse>('/api/auth/login', {
        provider,
        credentials,
      });

      if (response.error || !response.data) {
        logger.error('登录失败:', response.error);
        throw new Error(response.error || '登录失败');
      }

      // 保存Token
      apiClient.setToken(response.data.token);

      logger.info('登录成功:', response.data.user.username);
      return response.data;
    } catch (error: any) {
      logger.error('登录异常:', error);
      throw error;
    }
  }

  /**
   * 本地密码登录（快捷方法）
   */
  async loginWithPassword(username: string, password: string): Promise<LoginResponse | null> {
    return this.login('local', { username, password });
  }

  /**
   * 验证Session
   */
  async verifySession(): Promise<User | null> {
    try {
      const response = await apiClient.get<SessionResponse>('/api/auth/session');

      if (response.error || !response.data) {
        logger.warn('Session验证失败:', response.error);
        return null;
      }

      return response.data.user;
    } catch (error: any) {
      logger.error('Session验证异常:', error);
      return null;
    }
  }

  /**
   * 获取当前用户信息（包含账户）
   */
  async getMe(): Promise<MeResponse | null> {
    try {
      const response = await apiClient.get<MeResponse>('/api/auth/me');

      if (response.error || !response.data) {
        logger.error('获取用户信息失败:', response.error);
        return null;
      }

      return response.data;
    } catch (error: any) {
      logger.error('获取用户信息异常:', error);
      return null;
    }
  }

  /**
   * 登出
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout');
      
      // 清除Token
      apiClient.setToken(null);
      
      logger.info('登出成功');
    } catch (error: any) {
      logger.error('登出异常:', error);
      // 即使失败也清除本地Token
      apiClient.setToken(null);
    }
  }

  /**
   * 修改密码
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
    try {
      const response = await apiClient.post('/api/auth/change-password', {
        oldPassword,
        newPassword,
      });

      if (response.error) {
        logger.error('修改密码失败:', response.error);
        throw new Error(response.error);
      }

      logger.info('修改密码成功');
      return true;
    } catch (error: any) {
      logger.error('修改密码异常:', error);
      throw error;
    }
  }

  /**
   * 更新用户角色
   * ⚠️ 注意：角色更新后需要重新登录才能生效
   */
  async updateRole(role: 'trader' | 'broker' | 'admin'): Promise<boolean> {
    try {
      const response = await apiClient.put('/api/auth/update-role', {
        role,
      });

      if (response.error) {
        logger.error('更新角色失败:', response.error);
        throw new Error(response.error);
      }

      logger.info(`✅ 角色已更新为: ${role}`);
      return true;
    } catch (error: any) {
      logger.error('更新角色异常:', error);
      throw error;
    }
  }

  /**
   * 更新用户名
   * ⚠️ 注意：用户名更新后需要重新登录才能生效
   */
  async updateUsername(newUsername: string): Promise<{ success: boolean; username: string; message: string }> {
    try {
      const response = await apiClient.put<{ success: boolean; username: string; message: string }>('/api/auth/update-username', {
        newUsername,
      });

      if (response.error || !response.data) {
        logger.error('更新用户名失败:', response.error);
        throw new Error(response.error || '更新用户名失败');
      }

      logger.info(`✅ 用户名已更新为: ${response.data.username}`);
      return response.data;
    } catch (error: any) {
      logger.error('更新用户名异常:', error);
      throw error;
    }
  }

  /**
   * 标记推荐人弹窗已显示
   */
  async markReferrerPopupSeen(): Promise<boolean> {
    try {
      const response = await apiClient.put('/api/auth/mark-referrer-popup-seen');

      if (response.error) {
        logger.error('标记推荐人弹窗失败:', response.error);
        throw new Error(response.error);
      }

      logger.info('✅ 已标记推荐人弹窗为已看过');
      return true;
    } catch (error: any) {
      logger.error('标记推荐人弹窗异常:', error);
      throw error;
    }
  }

  /**
   * 设置推荐人
   */
  async setReferrer(referrerCode: string): Promise<boolean> {
    try {
      const response = await apiClient.post('/api/auth/set-referrer', {
        referrerCode,
      });

      if (response.error) {
        logger.error('设置推荐人失败:', response.error);
        throw new Error(response.error);
      }

      logger.info('✅ 推荐人设置成功');
      return true;
    } catch (error: any) {
      logger.error('设置推荐人异常:', error);
      throw error;
    }
  }

  /**
   * 检查是否已登录
   */
  isLoggedIn(): boolean {
    return apiClient.getToken() !== null;
  }

  /**
   * 获取当前Token
   */
  getToken(): string | null {
    return apiClient.getToken();
  }

  /**
   * 获取当前Session（从localStorage）
   */
  getSession(): any {
    // ✅ 优先从 current_session 获取完整session
    const sessionStr = localStorage.getItem('current_session');
    if (sessionStr) {
      try {
        const session = JSON.parse(sessionStr);
        // 确保session包含token（如果没有，从auth_token获取）
        if (!session.token) {
          const token = this.getToken();
          if (token) {
            session.token = token;
          }
        }
        return session;
      } catch {
        // 解析失败，继续尝试获取token
      }
    }
    
    // ✅ 兜底：如果没有current_session，只返回token
    const token = this.getToken();  // 从 auth_token 获取
    if (token) {
      return { token };
    }
    
    return null;
  }

  /**
   * 清除Session
   */
  clearSession(): void {
    localStorage.removeItem('sim_trading_session');
    localStorage.removeItem('current_session');
    apiClient.setToken(null);
    logger.info('✅ Session已清除');
  }
}

// 导出单例
export const authApi = new AuthApiService();

